import * as React from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import { AppSidebar } from "./app-sidebar";
import { AppLoading } from "./app-loading";
import { TopNavbar } from "./top-navbar";
import { Sheet, SheetContent } from "@repo/ui";
import { NotificationCenter } from "@/components/alerts/notification-center";
import { CreateReminderDialog } from "@/components/reminders/create-reminder-dialog";
import { PwaInstallPrompt } from "@/components/pwa-install-prompt";
import { EmailVerificationBanner } from "@/components/email-verification-banner";
import { AlertsProvider, useAlertsContext } from "@/context/alerts-context";
import {
  RemindersProvider,
  useRemindersContext,
} from "@/context/reminders-context";
import { ApprovalProvider } from "@/context/approval-context";
import { useProductTour } from "@/hooks/use-product-tour";
import { TourContext } from "@/context/tour-context";
import { useUserSessionTimeout } from "@/hooks/use-user-session-timeout";
import { useIdleTimeout } from "@/hooks/use-idle-timeout";
import { mapAlertSeverityToPriority } from "@/lib/utils/alert-to-reminder";
import { clearPassword } from "@/lib/api/auth";
import { getMyPermissionOverrides } from "@/lib/api/permissions";
import { useAuth } from "@/lib/auth/auth-provider";
import { usePharmacyStore } from "@/lib/stores/pharmacy-store";
import { usePermissionStore } from "@/lib/stores/permission-store";
import type { Alert } from "@/lib/types/alerts";
import type { Role } from "@/lib/permissions";

interface AppLayoutProps {
  children: React.ReactNode;
}

interface UserData {
  user: {
    id: string;
    name: string;
    role: string;
    initials: string;
  };
  pharmacy: {
    id: string;
    name: string;
  };
}

export function AppLayout({ children }: AppLayoutProps) {
  return (
    <AlertsProvider>
      <RemindersProvider>
        <ApprovalProvider>
          <AppLayoutInner>{children}</AppLayoutInner>
        </ApprovalProvider>
      </RemindersProvider>
    </AlertsProvider>
  );
}

function AppLayoutInner({ children }: AppLayoutProps) {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [notificationsOpen, setNotificationsOpen] = React.useState(false);
  const [remindingAlert, setRemindingAlert] = React.useState<Alert | null>(
    null
  );

  // Product tour — auto-starts on first dashboard visit
  const isDashboard = pathname === "/dashboard";
  const { startTour } = useProductTour({ autoStart: isDashboard });

  // Derive state from auth context — no redundant API call. AppLayout is
  // only mounted once the pharmacy has at least one staff user: the
  // needsFirstUser → CreateFirstUserModal path is handled upstream in
  // routes/_app.tsx so this layout never has to fetch user-scoped data
  // without an active user.
  const auth = useAuth();
  const userData: UserData | null =
    auth.user && auth.pharmacy
      ? {
          user: auth.user,
          pharmacy: { id: auth.pharmacy.id, name: auth.pharmacy.name },
        }
      : null;
  const emailVerified = auth.emailVerified;
  const pharmacyEmail = auth.email ?? auth.pharmacy?.email ?? "";
  const pharmacyName = auth.pharmacy?.name ?? "";

  const {
    alerts: alertData,
    counts: alertCounts,
    dismiss: dismissAlert,
    dismissAll: dismissAllAlerts,
  } = useAlertsContext();

  const {
    reminders,
    toggleComplete: completeReminder,
    createReminder,
    dueTodayCount,
    overdueCount,
  } = useRemindersContext();

  const reminderDueCount = dueTodayCount + overdueCount;

  // Initialize stores on mount (in-flight guards prevent StrictMode duplicates)
  const fetchPharmacy = usePharmacyStore((s) => s.fetchPharmacy);
  const fetchUsers = usePharmacyStore((s) => s.fetchUsers);
  const permissionIsLoaded = usePermissionStore((s) => s.isLoaded);
  const permissionCheck = usePermissionStore((s) => s.check);
  // Subscribe to overrides so permission checks re-evaluate when overrides are loaded
  const permissionOverrides = usePermissionStore((s) => s.overrides);
  const setOverrides = usePermissionStore((s) => s.setOverrides);

  React.useEffect(() => {
    fetchPharmacy();
  }, [fetchPharmacy]);

  // Fetch users only when permissions (including overrides) are loaded
  React.useEffect(() => {
    if (!auth.user?.role) return;
    // Wait until overrides are loaded so we respect runtime overrides
    if (!permissionIsLoaded) return;

    const perm = permissionCheck("users:view");
    if (perm && perm.type !== "denied") {
      fetchUsers();
    }
  }, [
    fetchUsers,
    auth.user?.role,
    permissionIsLoaded,
    permissionCheck,
    permissionOverrides,
  ]);

  // Fetch permission overrides for the current user's role
  React.useEffect(() => {
    if (permissionIsLoaded || !auth.user) return;

    let cancelled = false;
    async function loadOverrides() {
      try {
        const data = await getMyPermissionOverrides();
        if (!cancelled) {
          setOverrides(data?.overrides ?? []);
        }
      } catch {
        // Fall back to empty overrides so the app remains usable
        if (!cancelled) {
          setOverrides([]);
        }
      }
    }
    loadOverrides();
    return () => {
      cancelled = true;
    };
  }, [permissionIsLoaded, auth.user, setOverrides]);

  // Handle redirects based on auth state
  React.useEffect(() => {
    if (auth.isLoading) return;

    // No active user selected — redirect to select-user (unless on dashboard)
    if (!auth.user && auth.isAuthenticated) {
      const hasTabSession = sessionStorage.getItem("activeUser");
      if (!hasTabSession) {
        clearPassword().catch(() => {});
        navigate({ to: "/select-user" });
        return;
      }
      if (pathname !== "/dashboard") {
        navigate({ to: "/select-user" });
      }
    }
  }, [auth.isLoading, auth.user, auth.isAuthenticated, navigate, pathname]);

  // End user (staff) session when tab is closed; pharmacy session stays
  useUserSessionTimeout(!!userData);
  // End user (staff) session after 1 hour inactivity; pharmacy session stays
  useIdleTimeout(!!userData);

  const handleSettings = () => {
    navigate({ to: "/settings" });
  };

  const handleSwitchUser = async () => {
    try {
      // Clear the active user from session metadata
      await clearPassword();
      sessionStorage.removeItem("activeUser");
      // Redirect to user selection
      navigate({ to: "/select-user" });
    } catch (error) {
      console.error("Switch user error:", error);
      sessionStorage.removeItem("activeUser");
      navigate({ to: "/select-user" });
    }
  };

  const alertCount = alertCounts?.total ?? 0;

  // Show loading state only initially
  if (auth.isLoading) {
    return <AppLoading />;
  }

  // User data not available — show loading until auth settles
  if (!userData) {
    return <AppLoading />;
  }

  // Render dashboard content
  const renderDashboard = () => (
    <div className="flex h-screen flex-col overflow-hidden bg-background">
      {/* Top Navbar */}
      <TopNavbar
        user={
          userData?.user || {
            name: pharmacyName,
            role: "admin",
            initials: pharmacyName.substring(0, 2).toUpperCase(),
          }
        }
        pharmacy={userData?.pharmacy || { name: pharmacyName }}
        onSettings={handleSettings}
        onSwitchUser={handleSwitchUser}
        onMobileMenuClick={() => setMobileOpen(true)}
        onBellClick={() => setNotificationsOpen(true)}
        onStartTour={startTour}
        alertCount={alertCount}
      />

      {/* Content Area with Sidebar */}
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex">
          <AppSidebar
            alertCount={alertCount}
            reminderDueCount={reminderDueCount}
            userRole={userData?.user.role as Role | undefined}
            onSwitchUser={handleSwitchUser}
          />
        </aside>

        {/* Mobile Sidebar Drawer */}
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetContent side="left" className="p-0 w-64">
            <AppSidebar
              alertCount={alertCount}
              reminderDueCount={reminderDueCount}
              userRole={userData?.user.role as Role | undefined}
              onSwitchUser={() => {
                setMobileOpen(false);
                handleSwitchUser();
              }}
            />
          </SheetContent>
        </Sheet>

        {/* Main Content Area */}
        <main className="flex-1 overflow-y-auto">
          {!emailVerified && pharmacyEmail && (
            <EmailVerificationBanner email={pharmacyEmail} />
          )}
          <div className="mx-auto w-full max-w-7xl p-4 sm:p-6 lg:p-8 animate-in-page">
            {children}
          </div>
        </main>
      </div>
    </div>
  );

  return (
    <TourContext.Provider value={{ startTour }}>
      {renderDashboard()}
      <NotificationCenter
        open={notificationsOpen}
        onOpenChange={setNotificationsOpen}
        alerts={alertData}
        counts={alertCounts}
        reminders={reminders}
        onDismiss={dismissAlert}
        onDismissAll={dismissAllAlerts}
        onRemind={(alert) => setRemindingAlert(alert)}
        onCompleteReminder={(id) => completeReminder(id)}
      />
      <CreateReminderDialog
        open={!!remindingAlert}
        onClose={() => setRemindingAlert(null)}
        onSubmit={async (input) => {
          const result = await createReminder(input);
          if (result && remindingAlert) {
            await dismissAlert(remindingAlert.alertKey);
          }
          setRemindingAlert(null);
          return result;
        }}
        prefillTitle={
          remindingAlert
            ? `Alert: ${remindingAlert.title}${remindingAlert.itemName ? ` — ${remindingAlert.itemName}` : ""}`
            : undefined
        }
        prefillDescription={remindingAlert?.message}
        prefillPriority={
          remindingAlert
            ? mapAlertSeverityToPriority(remindingAlert.severity)
            : undefined
        }
        prefillVisibility="pharmacy"
      />
      <PwaInstallPrompt />
    </TourContext.Provider>
  );
}
